import {
	HTTP_INTERCEPTORS,
	HttpBackend,
	provideHttpClient,
	withInterceptorsFromDi,
	withXhr,
} from '@angular/common/http';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { environment } from '../environments/environment';
import {
	MissingTranslationHandler,
	MissingTranslationHandlerParams,
	TranslateDefaultParser,
	TranslateLoader,
	TranslateModule,
	TranslateModuleConfig,
	TranslateParser,
} from '@ngx-translate/core';
import {
	ApplicationConfig,
	importProvidersFrom,
	inject,
	Injectable,
	LOCALE_ID,
	provideAppInitializer,
	provideZonelessChangeDetection,
} from '@angular/core';
import {
	ACCOUNT_SETTINGS_CONFIG as ACCOUNT_SETTINGS_SHARED_CONFIG,
	ACCOUNT_SETTINGS_SERVICE_INJECTOR,
	AccountSettingsConfig,
	AlarisAccountSettingsService,
	AlarisApiService,
	AlarisAuthService,
	AlarisBalanceService,
	AlarisConfigService,
	AlarisDateFormatService,
	AlarisErrorInterceptor,
	AlarisFilesService,
	AlarisLanguageService,
	AlarisLocalStorageService,
	AlarisPasswordRestrictionsService,
	AlarisPaymentMethodsService,
	AlarisProfileService,
	AlarisResponseInterceptor,
	AlarisRpcInterceptor,
	AlarisSessionService,
	AlarisSignUpService,
	AlarisTableStateStore,
	AlarisWebSocketService,
	AUTH_SERVICE_INJECTOR,
	BALANCE_SERVICE_INJECTOR,
	CAPTCHA_CONFIG,
	CaptchaConfig,
	CONFIG_SERVICE_INJECTOR,
	DATE_FILTER_END_OF_THE_DAY_INJECTOR,
	DATE_FORMAT_SERVICE_INJECTOR,
	DateFormatPipe,
	DAY_TYPE_HANDLER,
	English,
	ENVIRONMENT,
	FILES_SERVICE_INJECTOR,
	ICONS_CONFIG,
	LANGUAGE_CONFIG,
	LANGUAGE_SERVICE_INJECTOR,
	LanguageConfig,
	LocaleIdService,
	LOCALSTORAGE_SERVICE_INJECTOR,
	MENU_MQ_SERVICE_INJECTOR,
	MENU_MQ_WIDTH_VALUE,
	MenuSidebarService,
	PASSWORD_RESTRICTIONS_SERVICE_INJECTOR,
	PAYMENT_METHODS_SERVICE_INJECTOR,
	PROFILE_CONFIG,
	PROFILE_SERVICE_INJECTOR,
	ProfileConfig,
	provideAlarisToast,
	REPO_SERVICE_INJECTOR,
	Russian,
	SESSION_SERVICE_INJECTOR,
	SIGN_IN_CONFIG,
	SIGN_IN_SERVICE_INJECTOR,
	SIGN_UP_CONFIG,
	SIGN_UP_SERVICE_INJECTOR,
	SignInConfig,
	SignUpConfig,
	Spain,
	TABLE_STATE_STORE_INJECTOR,
	Turkish,
	WEBSOCKET_SERVICE_INJECTOR,
} from '@campaign-portal/components-library';
import { RoleType } from '@campaign-portal/namespace/common/enums';
import { DATE_PIPE_DEFAULT_OPTIONS, DatePipe, registerLocaleData } from '@angular/common';
import { RepoService } from '@shared/repo';
import { DateFormatSyncService, ThemeModeService } from '@shared/services';
import { provideAnimations } from '@angular/platform-browser/animations';

import localeEn from '@angular/common/locales/en';
import localeEnExtra from '@angular/common/locales/extra/en';
import localeEs from '@angular/common/locales/es';
import localeEsExtra from '@angular/common/locales/extra/es';
import localeRu from '@angular/common/locales/ru';
import localeRuExtra from '@angular/common/locales/extra/ru';
import localeTr from '@angular/common/locales/tr';
import localeTrExtra from '@angular/common/locales/extra/tr';

import { provideRouter } from '@angular/router';
import { APP_ROUTES } from './app.routing';
import { PreventEventPlugin } from '@taiga-ui/event-plugins';
import { EVENT_MANAGER_PLUGINS } from '@angular/platform-browser';
import { OVERLAY_DEFAULT_CONFIG, OverlayDefaultConfig } from '@angular/cdk/overlay';
import { DS_ICONS_CONFIG } from '@alarislabs/angular-components/icon-swap';

import { PROFILE_PROVIDER } from '@campaign-portal/contracts/profile';
import { LANGUAGE_PROVIDER } from '@campaign-portal/contracts/language';
import { THEME_MODE_PROVIDER } from '@campaign-portal/contracts/theme';
import { SESSION_PROVIDER } from '@campaign-portal/contracts/session';
import { APP_INFO_PROVIDER } from '@campaign-portal/contracts/app-info';
import { PERSONAL_DATA_API } from '@campaign-portal/contracts/account-settings/personal-data';
import { PRIVACY_AND_SECURITY_API } from '@campaign-portal/contracts/account-settings/privacy-and-security';
import { NOTIFICATION_API } from '@campaign-portal/contracts/account-settings/notification';
import { ACCOUNT_SETTINGS_CONFIG } from '@campaign-portal/contracts/account-settings/config';
import {
	AlarisProfileAdapter,
	AlarisLanguageAdapter,
	AlarisSessionAdapter,
	AlarisAppInfoAdapter,
	ThemeModeAdapter,
	AlarisPersonalDataAdapter,
	AlarisPrivacyAndSecurityAdapter,
	AlarisNotificationAdapter,
} from '@shared/adapters';
import { GLOBAL_CONFIG, GlobalConfig } from '@campaign-portal/contracts/global-config';

/* Translations */
function HttpLoaderFactory(http: HttpBackend): MultiTranslateHttpLoader {
	return new MultiTranslateHttpLoader(http, [
		{ prefix: './assets/i18n_lib/app.', suffix: '.json?' + environment.version },
		{ prefix: './assets/i18n_ds-lib/app.', suffix: '.json?' + environment.version },
		{ prefix: './assets/i18n_shared/app.', suffix: '.json?' + environment.version },
		{ prefix: './assets/i18n/app.', suffix: '.json?' + environment.version },
	]);
}

class MissingTranslationService implements MissingTranslationHandler {
	handle(params: MissingTranslationHandlerParams): string {
		return params.key;
	}
}

@Injectable()
class TranslateParserService extends TranslateDefaultParser {
	override getValue(target: Record<string, unknown>, key: string): unknown {
		const result = super.getValue(target, key);
		return typeof result === 'object' && result !== null ? key : result;
	}
}

/* End of Translations */

/* forRoot() Configs */
const translateConfig: TranslateModuleConfig = {
	loader: {
		provide: TranslateLoader,
		useFactory: HttpLoaderFactory,
		deps: [HttpBackend],
	},
	missingTranslationHandler: {
		provide: MissingTranslationHandler,
		useClass: MissingTranslationService,
	},
	parser: { provide: TranslateParser, useClass: TranslateParserService },
	// if we set `defaultLanguage` to 'en' right here - we force loading en.json with translations
	// so missing translations will be used from en.file
	defaultLanguage: '',
};

const signInConfig: SignInConfig = {
	url: '',
	supportEmail: '',
	signInMethod: 'Auth.SignIn',
	refreshTokenMethod: 'Auth.Refresh',
	resetPasswordMethod: 'Auth.ResetPassword',
	verify2FACodeMethod: 'Auth.Verify2FACode',
	is2FA: false,
	signUpLink: '/sign-up',
};

const signUpConfig: SignUpConfig = {
	url: '',
	signInLink: '/sign-in',
	termsUrl: '',
	privacyUrl: '',
	supportEmail: '',
	signUpMethod: 'Auth.SignUp',
	registrationFieldsMethod: 'RegistrationFields.Read',
};

const captchaConfig: CaptchaConfig = {
	baseUrl: 'captcha',
};

const profileConfig: ProfileConfig = {
	profileRoute: '/account-settings-legacy',
	termsUrl: '',
	privacyUrl: '',
	supportEmail: '',
};

const accountSettingsConfig: AccountSettingsConfig = {
	resetPasswordMethod: 'Users.PersonalData.ResetPassword',
	verifyCurrentPasswordMethod: 'Users.VerifyCurrentPassword',
	validatePasswordMethod: 'PasswordRestrictions.PersonalData.Validate',
	userReadMethod: 'Users.PersonalData.Read',
	userUpdateMethod: 'Users.PersonalData.Update',
	verify2FACodeMethod: 'Auth.Verify2FACodeForReset',
	reset2FACodeMethod: 'Auth.GetQRForResetCodeForPortal',
	partnersReadMethod: 'Partners.Read',
	customFormsReadMethod: 'UserFields.Read',
	generatePasswordMethod: 'Users.GeneratePassword',
	role: RoleType.ADMIN,
	is2FA: false,
	showNoPermissionsWarning: false,
};

const languageConfig: LanguageConfig = { languages: [English, Spain], currentLanguage: English };
/* End of forRoot() Configs */

/* App Config */
function applyAppConfig(appConfig: AlarisConfigService): void {
	signInConfig.url = appConfig.api;
	signInConfig.supportEmail = appConfig.supportEmail;
	signInConfig.is2FA = appConfig.is2FA;

	accountSettingsConfig.is2FA = appConfig.is2FA;

	signUpConfig.url = appConfig.api;
	signUpConfig.termsUrl = appConfig.termsUrl;
	signUpConfig.privacyUrl = appConfig.privacyUrl;
	signUpConfig.supportEmail = appConfig.supportEmail;

	profileConfig.termsUrl = appConfig.termsUrl;
	profileConfig.privacyUrl = appConfig.privacyUrl;
	profileConfig.supportEmail = appConfig.supportEmail;

	languageConfig.languages = appConfig.getLanguages();
}

function applyLocaleConfig(store: AlarisLocalStorageService, translate: AlarisLanguageService): void {
	const lang = store.get('lang') ?? 'en';
	translateConfig.defaultLanguage = lang;
	registerLocaleData(localeEn, English.id, localeEnExtra);
	registerLocaleData(localeEs, Spain.id, localeEsExtra);
	registerLocaleData(localeRu, Russian.id, localeRuExtra);
	registerLocaleData(localeTr, Turkish.id, localeTrExtra);
	// https://mcvendrell.medium.com/configuring-ngx-translate-to-load-at-startup-in-angular-1995e7dd6fcc
	translate.set(lang);
}

function loadConfig(): Promise<void> {
	const appConfig = inject(AlarisConfigService);
	const store = inject(AlarisLocalStorageService);
	const translate = inject(AlarisLanguageService);
	const themeMode = inject(ThemeModeService);
	inject(DateFormatSyncService);

	return appConfig.load().then(() => {
		applyAppConfig(appConfig);
		applyLocaleConfig(store, translate);
		themeMode.init();
	});
}

/* End of App Config */

export const APP_CONFIG: ApplicationConfig = {
	providers: [
		importProvidersFrom(TranslateModule.forRoot(translateConfig)),
		provideAnimations(),
		provideHttpClient(withXhr(), withInterceptorsFromDi()),
		provideRouter(APP_ROUTES),
		provideAppInitializer(loadConfig),
		provideZonelessChangeDetection(),
		provideAlarisToast(),

		AlarisConfigService,
		AlarisLocalStorageService,
		AlarisApiService,
		AlarisAuthService,
		AlarisSignUpService,
		AlarisProfileService,
		AlarisLanguageService,
		AlarisFilesService,
		AlarisBalanceService,
		AlarisPaymentMethodsService,
		AlarisAccountSettingsService,
		AlarisPasswordRestrictionsService,
		AlarisTableStateStore,
		AlarisSessionService,
		AlarisDateFormatService,
		AlarisWebSocketService,

		{ provide: ENVIRONMENT, useValue: environment },
		{ provide: LOCALE_ID, useClass: LocaleIdService },
		{ provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: '+0', dateFormat: 'd MMM y, HH:mm' } },
		{ provide: EVENT_MANAGER_PLUGINS, multi: true, useClass: PreventEventPlugin },

		{ provide: CAPTCHA_CONFIG, useValue: captchaConfig },
		{ provide: LANGUAGE_CONFIG, useValue: languageConfig },
		{ provide: PROFILE_CONFIG, useValue: profileConfig },
		{ provide: ICONS_CONFIG, useValue: { baseUrl: '/assets/icons' } },
		{ provide: DS_ICONS_CONFIG, useValue: { baseUrl: '/assets/ds/icons' } },
		{ provide: ACCOUNT_SETTINGS_SHARED_CONFIG, useValue: accountSettingsConfig },
		{ provide: SIGN_IN_CONFIG, useValue: signInConfig },
		{ provide: SIGN_UP_CONFIG, useValue: signUpConfig },

		{ provide: LOCALSTORAGE_SERVICE_INJECTOR, useExisting: AlarisLocalStorageService },
		{ provide: CONFIG_SERVICE_INJECTOR, useExisting: AlarisConfigService },
		{ provide: REPO_SERVICE_INJECTOR, useExisting: RepoService },
		{ provide: AUTH_SERVICE_INJECTOR, useExisting: AlarisAuthService },
		{ provide: SIGN_IN_SERVICE_INJECTOR, useExisting: AlarisAuthService },
		{ provide: SIGN_UP_SERVICE_INJECTOR, useExisting: AlarisSignUpService },
		{ provide: PROFILE_SERVICE_INJECTOR, useExisting: AlarisProfileService },
		{ provide: LANGUAGE_SERVICE_INJECTOR, useExisting: AlarisLanguageService },
		{ provide: FILES_SERVICE_INJECTOR, useExisting: AlarisFilesService },
		{ provide: BALANCE_SERVICE_INJECTOR, useExisting: AlarisBalanceService },
		{ provide: PAYMENT_METHODS_SERVICE_INJECTOR, useExisting: AlarisPaymentMethodsService },
		{ provide: ACCOUNT_SETTINGS_SERVICE_INJECTOR, useExisting: AlarisAccountSettingsService },
		{ provide: PASSWORD_RESTRICTIONS_SERVICE_INJECTOR, useExisting: AlarisPasswordRestrictionsService },
		{ provide: TABLE_STATE_STORE_INJECTOR, useExisting: AlarisTableStateStore },
		{ provide: SESSION_SERVICE_INJECTOR, useExisting: AlarisSessionService },
		{ provide: DATE_FORMAT_SERVICE_INJECTOR, useExisting: AlarisDateFormatService },
		{ provide: WEBSOCKET_SERVICE_INJECTOR, useExisting: AlarisWebSocketService },

		{ provide: HTTP_INTERCEPTORS, useClass: AlarisRpcInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: AlarisResponseInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: AlarisErrorInterceptor, multi: true },

		{ provide: DAY_TYPE_HANDLER, useValue: (): string => 'weekday' },
		{ provide: DATE_FILTER_END_OF_THE_DAY_INJECTOR, useValue: true },
		{ provide: MENU_MQ_SERVICE_INJECTOR, useClass: MenuSidebarService },
		{ provide: MENU_MQ_WIDTH_VALUE, useValue: '(max-width: 1365px)' },
		{ provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } satisfies OverlayDefaultConfig },

		{ provide: PROFILE_PROVIDER, useExisting: AlarisProfileAdapter },
		{ provide: LANGUAGE_PROVIDER, useExisting: AlarisLanguageAdapter },
		{ provide: SESSION_PROVIDER, useExisting: AlarisSessionAdapter },
		{ provide: THEME_MODE_PROVIDER, useExisting: ThemeModeAdapter },
		{ provide: APP_INFO_PROVIDER, useExisting: AlarisAppInfoAdapter },
		{ provide: PERSONAL_DATA_API, useExisting: AlarisPersonalDataAdapter },
		{ provide: PRIVACY_AND_SECURITY_API, useExisting: AlarisPrivacyAndSecurityAdapter },
		{ provide: NOTIFICATION_API, useExisting: AlarisNotificationAdapter },
		{ provide: ACCOUNT_SETTINGS_CONFIG, useValue: { productName: 'Admin Panel' } },
		{ provide: GLOBAL_CONFIG, useValue: { localTime: false } satisfies GlobalConfig },

		DatePipe,
		DateFormatPipe,
	],
};
